import { createSearchSelect } from './search-select.js';

interface Options {
  search: Parameters<typeof createSearchSelect>[1];
  decreaseLabel: string;
  increaseLabel: string;
  sliderLabel: string;
  unansweredText: string;
}

/** Ordinal presentation only: labels and submitted values belong to the select. */
export function createRangePicker(select: HTMLSelectElement, options: Options) {
  if (select.multiple) throw new Error('Range picker requires a single select');
  const element = document.createElement('div'); element.className = 'range-picker';
  select.before(element);
  element.append(select);
  const search = createSearchSelect(select, options.search);
  const row = document.createElement('div'); row.className = 'range-picker-controls';
  const previous = document.createElement('button'); previous.type = 'button'; previous.className = 'range-picker-step';
  previous.textContent = '−'; previous.setAttribute('aria-label', options.decreaseLabel);
  const next = document.createElement('button'); next.type = 'button'; next.className = 'range-picker-step';
  next.textContent = '+'; next.setAttribute('aria-label', options.increaseLabel);
  row.append(previous, search.element, next);
  const slider = document.createElement('input'); slider.type = 'range'; slider.className = 'range-picker-slider';
  slider.min = '0'; slider.step = '1'; slider.setAttribute('aria-label', options.sliderLabel);
  const ticks = document.createElement('div'); ticks.className = 'range-picker-ticks'; ticks.setAttribute('aria-hidden', 'true');
  element.append(row, slider, ticks);
  let disposed = false;
  let choices: HTMLOptionElement[] = [];
  let tickSignature = '';
  const unavailable = () => select.matches(':disabled');
  const index = () => choices.findIndex(option => option.selected);
  function sync() {
    if (disposed) return;
    search.sync();
    choices = Array.from(select.options).filter(option => option.value !== '' && !option.hidden && !option.disabled
      && !(option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled));
    const current = index(), disabled = unavailable() || choices.length === 0;
    slider.max = String(Math.max(0, choices.length - 1));
    slider.value = String(Math.max(0, current));
    slider.disabled = disabled;
    previous.disabled = disabled || current <= 0;
    next.disabled = disabled || current === choices.length - 1;
    element.dataset.unanswered = String(current < 0);
    slider.setAttribute('aria-valuetext', current < 0 ? options.unansweredText : choices[current]!.label);
    slider.setAttribute('aria-required', String(select.required));
    const trigger = search.element.querySelector<HTMLButtonElement>('.search-select-trigger')!;
    for (const name of ['aria-invalid', 'aria-describedby', 'aria-errormessage']) {
      const value = select.getAttribute(name) ?? trigger.getAttribute(name);
      if (value === null) slider.removeAttribute(name); else slider.setAttribute(name, value);
    }
    const signature = JSON.stringify(choices.map(option => option.label));
    if (signature !== tickSignature) {
      tickSignature = signature;
      const count = Math.min(5, choices.length);
      ticks.replaceChildren(...Array.from({ length: count }, (_, position) => {
        const tick = document.createElement('span'); tick.className = 'range-picker-tick';
        const ordinal = Math.round(position * (choices.length - 1) / Math.max(1, count - 1));
        tick.style.left = `${100 * position / Math.max(1, count - 1)}%`;
        if (position === 0 || position === count - 1 || position === Math.floor(count / 2)) {
          tick.textContent = choices[ordinal]!.label;
          if (position !== 0 && position !== count - 1) tick.classList.add('range-picker-tick-middle');
        }
        return tick;
      }));
    }
  }
  const choose = (ordinal: number) => {
    if (disposed || unavailable()) { sync(); return; }
    const option = choices[Math.max(0, Math.min(choices.length - 1, Math.round(ordinal)))];
    if (!option || option.selected) return;
    select.selectedIndex = option.index;
    sync();
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const onPrevious = () => { if (index() > 0) choose(index() - 1); };
  const onNext = () => choose(index() + 1);
  // A click at the first notch may produce no native input event when blank.
  const onSlide = (event: Event) => { event.stopPropagation(); choose(Number(slider.value)); };
  const onKey = (event: KeyboardEvent) => {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
    const current = index(), last = choices.length - 1;
    const page = Math.max(1, Math.round(choices.length / 10));
    let target: number;
    switch (event.key) {
      case 'Home': target = 0; break;
      case 'End': target = last; break;
      case 'ArrowRight': case 'ArrowUp': target = current < 0 ? 0 : current + 1; break;
      case 'ArrowLeft': case 'ArrowDown': target = current < 0 ? 0 : current - 1; break;
      case 'PageUp': target = current < 0 ? 0 : current + page; break;
      case 'PageDown': target = current < 0 ? 0 : current - page; break;
      default: return;
    }
    event.preventDefault(); choose(target);
  };
  const onReset = () => queueMicrotask(sync);
  const form = select.form;
  previous.addEventListener('click', onPrevious); next.addEventListener('click', onNext);
  slider.addEventListener('input', onSlide); slider.addEventListener('change', onSlide);
  slider.addEventListener('pointerup', onSlide); slider.addEventListener('click', onSlide); slider.addEventListener('keydown', onKey);
  select.addEventListener('input', sync); select.addEventListener('change', sync); select.addEventListener('invalid', sync);
  form?.addEventListener('reset', onReset);
  const observer = new MutationObserver(records => {
    if (records.some(record => select.contains(record.target) || (record.target instanceof Element && record.target.contains(element)))) sync();
  });
  observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true,
    attributeFilter: ['disabled', 'required', 'aria-invalid', 'aria-describedby', 'aria-errormessage', 'hidden', 'label', 'selected', 'value'] });
  sync();
  return {
    element, sync,
    dispose() {
      if (disposed) return;
      disposed = true; observer.disconnect();
      previous.removeEventListener('click', onPrevious); next.removeEventListener('click', onNext);
      slider.removeEventListener('input', onSlide); slider.removeEventListener('change', onSlide);
      slider.removeEventListener('pointerup', onSlide); slider.removeEventListener('click', onSlide); slider.removeEventListener('keydown', onKey);
      select.removeEventListener('input', sync); select.removeEventListener('change', sync); select.removeEventListener('invalid', sync);
      form?.removeEventListener('reset', onReset);
      search.dispose();
      if (element.parentNode) element.replaceWith(select); else select.remove();
    },
  };
}
